// src/components/shared/EditTripPage.vue
<script setup>
import { useDataStore } from '@/stores/data'
import { useMapStore } from '@/stores/map'
import { useAppStore } from '@/stores/app'
import { watch, ref, computed, onBeforeUnmount } from 'vue'
import BasePage from './BasePage.vue'
import SelectBox from '@/components/shared/SelectBox.vue'
import googlePlacesService from '@/services/googlePlaces'
import cloneDeep from 'lodash/cloneDeep'
import enturService from '@/services/enturService'
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue'
import { ElPopover } from 'element-plus'
import 'element-plus/es/components/popover/style/css'

const dataStore = useDataStore()
const mapStore = useMapStore()
const appStore = useAppStore()
const hasChanged = ref(false)
const isValid = ref(false)

const defaultPublicTransportOptions = [
	{
		unknown: true,
		id: 'UNKNOWN',
		name: 'Vet ikke',
		locality: 'Ukjent',
		destination: 'Ukjent',
		transportMode: 'UNKNOWN',
	},
]

const selectedDestinationStop = ref(null)
const selectedOriginStop = ref(null)
const selectedLine = ref(null)
const destinationStopOptions = ref(defaultPublicTransportOptions)
const lineOptions = ref(defaultPublicTransportOptions)

const goBack = () => {
	appStore.goBack()
	setTimeout(() => {
		dataStore.currentEditTrip = {}
	}, 500)
}

const deleteTrip = () => {
	dataStore.deleteTrip(dataStore.currentTrip)
	goBack()
}

const changeLocation = (type) => {
	console.warn('EditTripPage: changeLocation', type, dataStore.currentEditTrip)
	appStore.bottomSheet.close(500, 'EditTripPage', false)
	dataStore.currentLocationType = type
	dataStore.showGeocoder = true
	var currentLocation = type === 'origin' ? dataStore.currentEditTrip.origin : dataStore.currentEditTrip.destination
	mapStore.currentEditTrip = cloneDeep(dataStore.currentEditTrip)
	mapStore.currentEditTripIsNew = dataStore.currentEditTrip.new
	mapStore.updateMapForEdit()
	if (currentLocation?.latitude && currentLocation?.longitude) {
		setTimeout(() => {
			mapStore.flyToLocation(currentLocation, 16, 500)
		}, 300)
	}
}

const saveTrip = () => {
	isValid.value = checkIsValid(true)
	if (isValid.value) {
		// If trip is not public transport we should remove the pt object property
		if (!isPublicTransport()) delete dataStore.currentEditTrip.pt

		if (dataStore.currentEditTrip.newjourney) {
			// When saving a new journey coming from intro page with not journeys, we should make sure we will go back to trips page showing the new journey
			if (appStore.previousPage == 'intro') {
				appStore.previousPage = 'trips'
			}
			dataStore.saveNewJourney()
		} else {
			dataStore.currentEditTrip.edits = tripEdits.value
			dataStore.saveTrip()
		}
		// TODO: Only add survey to this trip and reavaluate journey
		dataStore.addSurveyToCurrentJourney()
		appStore.editStep = ''
		mapStore.currentTrips = dataStore.currentJourney.trips
		mapStore.plotTrips()

		goBack()
	}
}

const fetchLocationPlace = async (locationType) => {
	console.warn('fetchLocationPlace', locationType)
	const location = dataStore.currentEditTrip[locationType]
	if (!location || !location.latitude || !location.longitude) {
		console.warn('fetchLocationPlace: No location data available for', locationType)
		return null
	}
	return await googlePlacesService.getPlaceOrAddressForLocation(location)
}

const checkHasChanged = () => {
	const original = dataStore.currentEditOriginal
	const current = dataStore.currentEditTrip
	if (dataStore.currentEditTrip?.new) return true
	if (!original || !current) return false
	if (original.activity_name !== current.activity_name) return true
	if (original.startTime !== current.startTime) return true
	if (original.endTime !== current.endTime) return true
	if (original.origin.latitude !== current.origin.latitude) return true
	if (original.origin.longitude !== current.origin.longitude) return true
	if (original.destination.latitude !== current.destination.latitude) return true
	if (original.destination.longitude !== current.destination.longitude) return true
	if (original.comment !== current.comment) return true
	return false
}

const checkIsValid = (checkBeforeSave) => {
	console.warn('checkIsValid', dataStore.currentEditTrip)

	if (dataStore.currentEditTrip) {
		// Custom check for stationary day
		if (dataStore.currentEditTrip.stationary) {
			// Just check if we have start time and origin by default for stationary day
			if (!dataStore.currentEditTrip.startTime) return false
			if (!dataStore.currentEditTrip.startDateString) return false
			if (!dataStore.currentEditTrip?.origin?.latitude) return false

			// If didleave is true, we also need to check if end time and destination are set
			if (dataStore.currentEditTrip.didleave) {
				if (!dataStore.currentEditTrip.endTime) return false
				if (!dataStore.currentEditTrip.endDateString) return false
				if (!dataStore.currentEditTrip?.destination?.latitude) return false
			}
		} else {
			if (!dataStore.currentEditTrip.activity_name) return false
			if (!dataStore.currentEditTrip.startTime) return false
			if (!dataStore.currentEditTrip.endTime) return false
			if (!dataStore.currentEditTrip?.origin?.latitude) return false
			if (!dataStore.currentEditTrip?.origin?.longitude) return false
			if (!dataStore.currentEditTrip?.destination?.latitude) return false
			if (!dataStore.currentEditTrip?.destination?.longitude) return false
			if (isPublicTransport()) {
				if (!dataStore.currentEditTrip.pt) return false
				if (!dataStore.currentEditTrip.pt.origin_stop_id) return false
				if (!dataStore.currentEditTrip.pt.destination_stop_id) return false
				if (!dataStore.currentEditTrip.pt.line_id) return false
			}
		}

		return true
	}
	return false
}

const fetchAndSetPlace = async (trip, type) => {
	// Ensure that currentEditTrip and the specific type property exist
	if (!trip || !trip[type]) {
		console.warn(`fetchAndSetPlace: currentEditTrip or ${type} data is missing.`)
		return
	}
	const originDestination = trip[type]
	if (!originDestination.place_id) {
		const place = await fetchLocationPlace(type)
		if (!place) {
			console.warn(`fetchAndSetPlace: No ${type} place found`)
			return
		}
		originDestination.title = place.title
		originDestination.subTitle = place.subTitle
		originDestination.type = place.type
		originDestination.id = place.id
		originDestination.placeTypes = place.placeTypes
		originDestination.categories = place.categories
	}
}

const fetchOriginDescription = async () => {
	fetchAndSetPlace(dataStore.currentEditTrip, 'origin')
}

const fetchDestinationDescription = async () => {
	fetchAndSetPlace(dataStore.currentEditTrip, 'destination')
}

const fetchOriginAndDestinationDescription = async () => {
	await fetchOriginDescription()
	await fetchDestinationDescription()
}

const updatePublicTransport = () => {
	console.warn('updatePublicTransport')
	if (!dataStore.currentEditTrip.pt) {
		dataStore.currentEditTrip.pt = {}
	}
	// Clear existing public transport values
	dataStore.currentEditTrip.pt.origin_stop_id = ''
	dataStore.currentEditTrip.pt.origin_stop_name = ''
	dataStore.currentEditTrip.pt.destination_stop_id = ''
	dataStore.currentEditTrip.pt.destination_stop_name = ''
	dataStore.currentEditTrip.pt.public_code = ''
	dataStore.currentEditTrip.pt.destination_name = ''
	dataStore.currentEditTrip.pt.line_id = ''
	dataStore.currentEditTrip.transport_mode = ''

	if (selectedOriginStop.value) {
		dataStore.currentEditTrip.pt.origin_stop_id = selectedOriginStop.value.id
		dataStore.currentEditTrip.pt.origin_stop_name = selectedOriginStop.value.name
		dataStore.currentEditTrip.origin.title = selectedOriginStop.value.name
		dataStore.currentEditTrip.origin.subTitle = `${selectedOriginStop.value.locality || ''}${selectedOriginStop.value.locality && selectedOriginStop.value.county ? ', ' : ''}${selectedOriginStop.value.county || ''}`
		if (!selectedOriginStop.value?.unknown) {
			dataStore.currentEditTrip.origin.latitude = selectedOriginStop.value.latitude
			dataStore.currentEditTrip.origin.longitude = selectedOriginStop.value.longitude
			dataStore.currentEditTrip.locations[0] = {
				latitude: dataStore.currentEditTrip.origin.latitude,
				longitude: dataStore.currentEditTrip.origin.longitude,
			}
		}
	}
	if (selectedDestinationStop.value) {
		dataStore.currentEditTrip.pt.destination_stop_id = selectedDestinationStop.value.id
		dataStore.currentEditTrip.pt.destination_stop_name = selectedDestinationStop.value.name
		dataStore.currentEditTrip.destination.title = selectedDestinationStop.value.name
		dataStore.currentEditTrip.destination.subTitle = `${selectedDestinationStop.value.locality || ''}${selectedDestinationStop.value.locality && selectedDestinationStop.value.county ? ', ' : ''}${selectedDestinationStop.value.county || ''}`
		if (!selectedDestinationStop.value?.unknown) {
			dataStore.currentEditTrip.destination.latitude = selectedDestinationStop.value.latitude
			dataStore.currentEditTrip.destination.longitude = selectedDestinationStop.value.longitude
			dataStore.currentEditTrip.locations[dataStore.currentEditTrip.locations.length - 1] = {
				latitude: dataStore.currentEditTrip.destination.latitude,
				longitude: dataStore.currentEditTrip.destination.longitude,
			}
		}
	}
	if (selectedLine.value) {
		dataStore.currentEditTrip.pt.public_code = selectedLine.value.name
		dataStore.currentEditTrip.pt.destination_name = selectedLine.value.destination
		dataStore.currentEditTrip.pt.line_id = selectedLine.value.id
		dataStore.currentEditTrip.pt.transport_mode = selectedLine.value.transportMode
	}

	console.warn('updatePublicTransport', dataStore.currentEditTrip.pt, selectedOriginStop.value, selectedDestinationStop.value, selectedLine.value)

	if (selectedDestinationStop.value && selectedOriginStop.value && !selectedDestinationStop.value.unknown && !selectedOriginStop.value.unknown) {
		const stopIndex = destinationStopOptions.value.findIndex((stop) => stop.id === selectedDestinationStop.value.id)
		console.warn('stopIndex', stopIndex)
		if (stopIndex > 0) {
			var firstStopLocation = [
				{
					latitude: selectedOriginStop.value.latitude,
					longitude: selectedOriginStop.value.longitude,
				},
			]
			const stopLocations = destinationStopOptions.value.slice(0, stopIndex + 1).map((stop) => {
				return {
					latitude: stop.latitude,
					longitude: stop.longitude,
				}
			})
			dataStore.currentEditTrip.locations = firstStopLocation.concat(stopLocations)
			console.warn('locations from destinationStopOptions', dataStore.currentEditTrip.locations)
		}
	}

	mapStore.currentEditTrip = cloneDeep(dataStore.currentEditTrip)
}

const initPublicTransport = async () => {
	if (dataStore.currentEditTrip.pt) {
		if (dataStore.currentEditTrip.pt.origin_stop_id) {
			selectedOriginStop.value = {
				id: dataStore.currentEditTrip.pt.origin_stop_id,
				name: dataStore.currentEditTrip.pt.origin_stop_name,
				latitude: dataStore.currentEditTrip.origin.latitude,
				longitude: dataStore.currentEditTrip.origin.longitude,
			}
			//getLinesForStopPlace(selectedOriginStop.value)
		}
		if (dataStore.currentEditTrip.pt.public_code) {
			selectedLine.value = {
				id: dataStore.currentEditTrip.pt.line_id,
				name: dataStore.currentEditTrip.pt.public_code,
				destination: dataStore.currentEditTrip.pt.destination_name,
				transportMode: dataStore.currentEditTrip.transport_mode,
			}
			//getStopsForLine(selectedLine.value)
		}
		if (dataStore.currentEditTrip.pt.destination_stop_id) {
			selectedDestinationStop.value = {
				id: dataStore.currentEditTrip.pt.destination_stop_id,
				name: dataStore.currentEditTrip.pt.destination_stop_name,
				latitude: dataStore.currentEditTrip.destination.latitude,
				longitude: dataStore.currentEditTrip.destination.longitude,
			}
		}
	}
	console.warn('initPublicTransport', dataStore.currentEditTrip.pt, selectedOriginStop.value, selectedDestinationStop.value, selectedLine.value)
}

const isTransportModePopoverVisible = ref(false)

const selectTransportMode = async (type) => {
	dataStore.currentEditTrip.activity_name = type
	dataStore.currentEditTrip.activity_source = 'edit'
	isTransportModePopoverVisible.value = false

	// Reset line and destination selection
	selectedLine.value = null
	selectedOriginStop.value = null
	selectedDestinationStop.value = null
	destinationStopOptions.value = defaultPublicTransportOptions

	if (!isPublicTransport()) {
		if (!dataStore.currentEditTrip?.origin?.place_id || !dataStore.currentEditTrip?.destination?.place_id) {
			// Make sure origin and destination have latitude and longitude
			if (dataStore.currentEditTrip?.origin?.latitude && dataStore.currentEditTrip?.origin?.longitude && dataStore.currentEditTrip?.destination?.latitude && dataStore.currentEditTrip?.destination?.longitude) {
				await fetchOriginAndDestinationDescription()
			}
		}
	} else {
		checkPublicTransport()
	}

	addRouteData()
	//initPublicTransport()
	//mapStore.plotCurrentEdit()
}

const isPublicTransport = () => {
	const isPublic = ['tram', 'bus', 'train', 'metro', 'water'].includes(dataStore.currentEditTrip.activity_name)
	return isPublic
}

const searchStopPlaces = async (query) => {
	var options = { types: [dataStore.currentEditTrip.activity_name] }
	var stopPlaces = await enturService.searchStopPlaces(query, options)
	stopPlaces = defaultPublicTransportOptions.concat(stopPlaces)
	return stopPlaces
}

const getLinesForStopPlace = async (originStop, destinationStop = null) => {
	if (!originStop || !originStop.id || originStop?.unknown) return
	const lines = await enturService.getLinesForStopPlace(originStop.id, destinationStop?.id, dataStore.currentEditTrip.activity_name)
	console.warn('getLinesForStopPlace', originStop, destinationStop, lines)
	lineOptions.value = lines.sort((a, b) => a.name.localeCompare(b.name))

	// Set selectedLine.value directly if only one line is found
	if (lineOptions.value.length === 1) {
		selectedLine.value = lineOptions.value[0]
		// Trigger change of line
		handleLineChange(selectedLine.value)
	}

	// Always add default option at the beginning
	lineOptions.value = defaultPublicTransportOptions.concat(lineOptions.value)
	console.warn('getLinesForStopPlace', lineOptions.value)
}

const getStopsForLine = async (line) => {
	if (!selectedOriginStop.value || !selectedOriginStop.value.id) return
	const stops = await enturService.getStopsForLine(line.id, selectedOriginStop.value.id)
	console.warn('getStopsForLine', line, stops)
	// Add default option at the beginning
	destinationStopOptions.value = defaultPublicTransportOptions.concat(stops)
}

const handleLineChange = async (line) => {
	console.warn('handleLineChange', line)
	selectedLine.value = line
	if (!selectedDestinationStop.value?.unknown) selectedDestinationStop.value = null
	updatePublicTransport()
	if (!line.unknown) getStopsForLine(line)
}

const handleDestinationChange = async (stop) => {
	console.warn('handleOriginDestinationChange', selectedDestinationStop.value, stop)

	// If line has not been selected, but both origin and destination stops are selected, try to find a matching line
	if (!selectedLine.value) {
		// TODO: Find line based on origin and destination stops
	}

	updatePublicTransport()

	// Update map view
	mapStore.plotCurrentEdit()
}

// Handler for when origin stop is selected
const handleOriginStopChange = async (stop) => {
	console.warn('handleOriginStopChange', stop)

	// Reset line and destination selection
	if (!selectedLine.value?.unknown) selectedLine.value = null
	if (!selectedDestinationStop.value?.unknown) selectedDestinationStop.value = null
	destinationStopOptions.value = defaultPublicTransportOptions

	// Update currentEditTrip with selected origin stop
	updatePublicTransport()

	// Fetch lines for the selected origin stop
	getLinesForStopPlace(stop)

	// Update map view
	mapStore.plotCurrentEdit()
}

const flyToLocation = (locationType) => {
	const location = dataStore.currentEditTrip[locationType]
	mapStore.flyToLocation(location, 16, 1000)
}

const checkPublicTransport = async () => {
	console.warn('checkPublicTransport')
	if (isPublicTransport()) {
		const firstLocation = dataStore.currentEditTrip.locations[0]
		firstLocation.hacc = 1
		const lastLocation = dataStore.currentEditTrip.locations[dataStore.currentEditTrip.locations.length - 1]
		lastLocation.hacc = 1
		const publicTransport = await enturService.checkPublicTransport(firstLocation, lastLocation, { transportMode: dataStore.currentEditTrip.activity_name })
		console.warn('checkPublicTransport', publicTransport)

		if (publicTransport && publicTransport.hasPublicTransport) {
			dataStore.currentEditTrip.pt = {
				origin_stop_id: publicTransport.originStop.id,
				origin_stop_name: publicTransport.originStop.name,
				destination_stop_id: publicTransport.destinationStop.id,
				destination_stop_name: publicTransport.destinationStop.name,
				line_id: publicTransport.line.id,
				public_code: publicTransport.line.code,
				destination_name: publicTransport.destination,
				transport_mode: publicTransport.line.transportMode,
			}
			console.warn('checkPublicTransport', dataStore.currentEditTrip.pt)
			dataStore.currentEditTrip.distance = publicTransport.distance
			dataStore.currentEditTrip.duration = publicTransport.duration
			const intermediateLocations = publicTransport.intermediateQuays.map((quay) => {
				return {
					public_transport: true,
					latitude: quay.latitude,
					longitude: quay.longitude,
					hacc: 1,
				}
			})
			dataStore.currentEditTrip.locations = [firstLocation].concat(intermediateLocations).concat([lastLocation])
			console.warn('checkPublicTransport', dataStore.currentEditTrip)
			initPublicTransport()
		}
	} else {
		// Remove public transport locations
		dataStore.currentEditTrip.locations = dataStore.currentEditTrip.locations.filter((location) => location.public_transport !== true)
		// Remove pt data
		delete dataStore.currentEditTrip.pt
	}
	mapStore.plotCurrentEdit()
}

const mapLocationHandler = (e) => {
	console.warn('EditTripPage: map-location', e.detail)
	const location = e.detail
	if (!dataStore.currentEditTrip.origin?.type || !dataStore.currentEditTrip.destination?.type) {
		googlePlacesService.getPlaceOrAddressForLocation(location).then((place) => {
			console.warn('setCoordinateChangeListener: getPlaceForLocation', location, place, dataStore.currentEditTrip.origin, dataStore.currentEditTrip.origin?.type)
			if (!dataStore.currentEditTrip.origin?.type) {
				dataStore.currentEditTrip.origin = setJourneyDestinationDataFromPlace(dataStore.currentEditTrip.origin, place)
			} else if (!dataStore.currentEditTrip.destination?.type) {
				dataStore.currentEditTrip.destination = setJourneyDestinationDataFromPlace(dataStore.currentEditTrip.destination, place)

				// Add locations if we have none
				dataStore.currentEditTrip.locations = [dataStore.currentEditTrip.origin, dataStore.currentEditTrip.destination]
				// Plot the trip
				mapStore.plotCurrentEdit()
			}
		})
	}
}

const setJourneyDestinationDataFromPlace = (destination, place) => {
	destination.place_id = place.place_id ?? place.id
	destination.type = place.type
	destination.title = place.title
	destination.subTitle = place.subTitle
	destination.categories = place.categories
	destination.latitude = place.latitude
	destination.longitude = place.longitude
	destination.placeTypes = place.placeTypes
	destination.formattedAddress = place.formattedAddress

	mapStore.plotTripsInner([dataStore.currentEditTrip])
	mapStore.flyToLocation(destination, 16, 1000)
	return destination
}

const deinit = () => {
	console.warn('EditTripPage: deinit')
	window.removeEventListener('map-location', mapLocationHandler)
}

const init = async () => {
	console.warn('EditTripPage init')
	mapStore.currentEditTripIsNew = dataStore.currentEditTrip.new
	if (isPublicTransport()) {
		initPublicTransport()
	}
	// If trip is not new and does not have place data for origin and destination, fetch it
	if (!dataStore.currentEditTrip.new) {
		if (!isPublicTransport()) {
			if (!dataStore.currentEditTrip?.origin?.place_id || !dataStore.currentEditTrip?.destination?.place_id) {
				await fetchOriginAndDestinationDescription()
			}
		}
	} else {
		dataStore.currentEditTrip.created_timestamp = new Date().getTime()
		dataStore.currentEditTrip.created = true

		if (dataStore.currentJourney && dataStore.currentEditTrip) {
			if (dataStore.currentEditTrip.newjourney) {
				console.warn('EditTripPage: new journey')
				// Add new journey AFTER current journey
				if (dataStore.currentEditTrip.orderjourney > 0) {
					// For a new journey, set the origin as the destination of the last trip
					dataStore.currentEditTrip.origin = dataStore.currentLastTrip.destination
					await fetchOriginDescription()
					mapStore.setCurrentEditTripLocationForPlace(dataStore.currentEditTrip.origin, 'origin')
					flyToLocation('origin')
				}
				// Add new journey before current journey
				if (dataStore.currentEditTrip.orderjourney < 0) {
					// For a new journey, set the destination as the origin of the first trip
					dataStore.currentEditTrip.destination = dataStore.currentFirstTrip.origin
					await fetchDestinationDescription()
					mapStore.setCurrentEditTripLocationForPlace(dataStore.currentEditTrip.destination, 'destination')
					flyToLocation('destination')
				}
			} else {
				// For a new trip use previous or next trip as default origin or destination
				if (dataStore.currentJourney.addIndex == 0) {
					dataStore.currentEditTrip.destination = cloneDeep(dataStore.currentJourney.trips[dataStore.currentJourney.addIndex]?.origin)
					await fetchDestinationDescription()
					mapStore.setCurrentEditTripLocationForPlace(dataStore.currentEditTrip.destination, 'destination')
					flyToLocation('destination')
				} else {
					dataStore.currentEditTrip.origin = cloneDeep(dataStore.currentJourney.trips[dataStore.currentJourney.addIndex - 1]?.destination)
					await fetchOriginDescription()
					mapStore.setCurrentEditTripLocationForPlace(dataStore.currentEditTrip.origin, 'origin')
					flyToLocation('origin')
				}
			}
		}
	}

	// Listen for coordinate long press on the map
	window.removeEventListener('map-location', mapLocationHandler)
	window.addEventListener('map-location', mapLocationHandler)

	setTimeout(() => {
		appStore.bottomSheet.autoResize(500, true, true, 'EditTripPage init')
	})
	console.warn('edittrip page init')
}

var firstLocation = null
var lastLocation = null

const addRouteData = async () => {
	if (dataStore.currentEditTrip && (dataStore.currentEditTrip?.new || dataStore.currentEditTrip?.created)) {
		console.warn('EditTripPage: addRouteData')
		if (!isPublicTransport()) {
			if (dataStore.currentEditTrip?.origin?.latitude && dataStore.currentEditTrip?.destination?.latitude) {
				firstLocation = cloneDeep(dataStore.currentEditTrip.origin)
				lastLocation = cloneDeep(dataStore.currentEditTrip.destination)
				await dataStore.addRouteDataForEditedTrip()
				setTimeout(() => {
					appStore.bottomSheet.autoResize(300, true, true, 'EditTripPage Watch')
				}, 500)
			}
		} else {
			// Remove location with source "route" if it exists
			dataStore.currentEditTrip.locations = dataStore.currentEditTrip.locations.filter((location) => location.source !== 'route')
			// Add origin and destination to locations
			if (firstLocation) {
				dataStore.currentEditTrip.locations.unshift(cloneDeep(firstLocation))
				firstLocation = null
			}
			if (lastLocation) {
				dataStore.currentEditTrip.locations.push(cloneDeep(lastLocation))
				lastLocation = null
			}
		}
	}
}

// Computed title
const pageTitle = computed(() => {
	if (dataStore.currentEditTrip?.stationary) {
		return 'Dag uten reiser'
	} else if (dataStore.currentEditTrip?.newjourney) {
		return 'Første etappe for helt ny reise'
	} else if (dataStore.currentEditTrip?.new) {
		return 'Ny etappe'
	} else {
		return 'Rediger etappe'
	}
})

const tripEdits = ref({})

// Function to track changes by comparing with original
const trackChangesFromOriginal = () => {
	if (!dataStore.currentEditTrip || !dataStore.currentEditOriginal) {
		return
	}

	// Properties to track (direct properties)
	const directProps = ['activity_name', 'startTime', 'endTime', 'origin_timestamp', 'destination_timestamp']

	// Check direct properties
	directProps.forEach((prop) => {
		compareAndTrackProperty(prop, dataStore.currentEditTrip[prop], dataStore.currentEditOriginal[prop])
	})

	// Nested objects to track
	const nestedObjects = [
		{
			path: 'origin',
			props: ['latitude', 'longitude', 'title', 'subTitle', 'place_id', 'type'],
		},
		{
			path: 'destination',
			props: ['latitude', 'longitude', 'title', 'subTitle', 'place_id', 'type'],
		},
		{
			path: 'pt',
			props: ['origin_stop_id', 'origin_stop_name', 'destination_stop_id', 'destination_stop_name', 'line_id', 'public_code', 'destination_name', 'transport_mode'],
		},
	]

	// Check nested properties
	nestedObjects.forEach((obj) => {
		const currentObj = dataStore.currentEditTrip[obj.path]
		const originalObj = dataStore.currentEditOriginal[obj.path]

		if (currentObj && originalObj) {
			obj.props.forEach((prop) => {
				const fullPath = `${obj.path}.${prop}`
				compareAndTrackProperty(fullPath, currentObj[prop], originalObj[prop])
			})
		}
	})
}

// Helper function to compare and track a single property
const compareAndTrackProperty = (propPath, currentValue, originalValue) => {
	// Skip if new value is undefined
	if (currentValue === undefined) return

	// Handle undefined values
	if (originalValue === undefined) originalValue = null

	// Compare values safely (handles objects, arrays, and primitives)
	const areEqual = JSON.stringify(currentValue) === JSON.stringify(originalValue)

	if (!areEqual) {
		// Check if we already have this property in tripEdits
		if (tripEdits.value[propPath]) {
			// If current value now matches original again, remove the edit
			if (JSON.stringify(currentValue) === JSON.stringify(tripEdits.value[propPath].originalValue)) {
				delete tripEdits.value[propPath]
			} else {
				// Just update the new value and timestamp
				tripEdits.value[propPath].newValue = cloneDeep(currentValue)
				tripEdits.value[propPath].timestamp = new Date().getTime()
			}
		} else {
			// Create new edit entry
			tripEdits.value[propPath] = {
				originalValue: cloneDeep(originalValue),
				newValue: cloneDeep(currentValue),
				timestamp: new Date().getTime(),
			}
		}
	} else {
		// If the values are equal, remove the edit entry if it exists
		if (tripEdits.value[propPath]) {
			delete tripEdits.value[propPath]
		}
	}
}

// Watch startTime and endTime for changes
watch(
	() => [dataStore.currentEditTrip?.startTime, dataStore.currentEditTrip?.endTime],
	async (newValue, oldValue) => {
		if (dataStore.currentEditTrip?.startTime && dataStore.currentEditTrip?.endTime) {
			dataStore.updateTimeStampsFromTimes(dataStore.currentEditTrip)
		}
	},
)

// Watch for changes in destination or origin
watch(
	() => [dataStore.currentEditTrip?.origin, dataStore.currentEditTrip?.destination],
	async (newValue, oldValue) => {
		if (newValue[0]?.latitude && newValue[0]?.longitude && newValue[1]?.latitude && newValue[1]?.longitude) {
			console.warn('EditTripPage: watch origin and destination', newValue, oldValue)
			addRouteData()
		}
	},
)

// Watch dataStore.currentEditTrip.didleave and resize bottom sheet if its true
watch(
	() => dataStore.currentEditTrip?.didleave,
	async (didleave) => {
		if (didleave) {
			setTimeout(() => {
				appStore.bottomSheet.autoResize(300, true, true, 'EditTripPage Watch didleave')
			})
		}
	},
)

// Add this watcher for changes to the currentEditTrip object
watch(
	() => dataStore.currentEditTrip,
	() => {
		// First track all changes by comparing with the original
		if (!dataStore.currentEditTrip?.new) {
			trackChangesFromOriginal()
			console.warn('tripEdits', tripEdits.value)
		}

		// Then update the hasChanged and isValid flags
		hasChanged.value = checkHasChanged()
		isValid.value = checkIsValid()
	},
	{ deep: true },
)
</script>

<template>
	<BasePage name="edit" @active="init" @inactive="deinit" :title="pageTitle">
		<template v-if="dataStore.currentEditTrip">
			<div class="mb-3 mt-1" v-if="!dataStore.currentEditTrip?.stationary">
				<label><strong>Transportmiddel for denne etappen</strong></label>
				<ElPopover placement="bottom" width="calc(100vw - 3rem)" trigger="click" v-model:visible="isTransportModePopoverVisible">
					<template #reference>
						<div class="form-control place mt-2">
							<span class="badge badge-pill activity" :class="[dataStore.currentEditTrip?.activity_name]"> <i class="fas fa-circle"></i></span>
							&nbsp; {{ dataStore.transportModes.find((m) => m.type === dataStore.currentEditTrip?.activity_name)?.name }}
						</div>
					</template>
					<div class="flex flex-wrap gap-2 mt-2" @click="visible = false">
						<div v-for="mode in dataStore.transportModes" :key="mode.type" class="button pr-3 pl-3 transportButton" :class="[mode.type, { selected: dataStore.currentEditTrip?.activity_name === mode.type }]" @click="selectTransportMode(mode.type)">
							<span class="badge badge-pill activity" :class="[mode.type]">
								<i class="fas fa-circle"></i>
							</span>
							{{ mode.name }}
						</div>
					</div>
				</ElPopover>
			</div>

			<div v-if="isPublicTransport()" class="pt-form">
				<div class="mb-4">
					<label><strong>Start</strong></label>
					<div class="input-group mt-1">
						<select-box class="form origin" searchable cols :options="defaultPublicTransportOptions" :rememberSearchQuery="true" :min-search-length="2" :search-debounce="400" :remote-search="searchStopPlaces" v-model="selectedOriginStop" @change="handleOriginStopChange">
							<template #selected-content="{ selectedValue }">
								<div v-if="selectedValue?.id">
									<template v-if="!selectedValue?.unknown">
										<div class="name">{{ selectedValue.name }}</div>
										<span>Holdeplass {{ selectedValue.locality }}</span>
									</template>
									<template v-else>
										<div class="name">{{ selectedValue.name }}</div>
										<span>Ukjent holdeplass</span>
									</template>
								</div>
								<div v-else>
									<div class="name">Velg start</div>
									<span>Søk etter holdeplasser</span>
								</div>
							</template>
							<template #item="{ option }">
								<div class="image" v-if="option?.category?.icon">
									<i class="fas" :class="option.category.icon"></i>
								</div>
								<div class="name">
									{{ option.name }}
								</div>
								<span>{{ option.locality }} {{ option.description }}</span>
							</template>
						</select-box>
					</div>
				</div>
				<div class="mb-4">
					<label><strong>Linje</strong></label>
					<div class="input-group mt-1">
						<select-box class="form origin" cols searchable :options="lineOptions" v-model="selectedLine" @change="handleLineChange">
							<template #selected-content="{ selectedValue }">
								<div v-if="selectedValue?.name">
									<template v-if="!selectedValue?.unknown">
										<div class="name">Linje {{ selectedValue?.name }}</div>
										<span>Retning {{ selectedValue?.destination }}</span>
									</template>
									<template v-else>
										<div class="name">{{ selectedValue?.name }}</div>
										<span>Ukjent linje</span>
									</template>
								</div>
								<div v-else>
									<div class="name">Velg linje</div>
									<span>Velge linje</span>
								</div>
							</template>
							<template #item="{ option }">
								<template v-if="!option?.unknown">
									<div class="name">{{ option.name }}</div>
									<span>{{ option.destination }}</span>
								</template>
								<template v-else>
									<div class="name">Vet ikke</div>
									<span>Ukjent linje</span>
								</template>
							</template>
						</select-box>
					</div>
				</div>
				<div class="mt-2 mb-4">
					<label for="startTime"><strong>Slutt</strong></label>
					<div class="input-group mt-1">
						<select-box class="form origin" searchable cols :options="destinationStopOptions" :rememberSearchQuery="true" :min-search-length="2" :search-debounce="400" :remote-search="searchStopPlaces" v-model="selectedDestinationStop" @change="handleDestinationChange">
							<template #selected-content="{ selectedValue }">
								<div v-if="selectedValue?.id">
									<template v-if="!selectedValue?.unknown">
										<div class="name">{{ selectedValue.name }}</div>
										<span>Holdeplass</span>
									</template>
									<template v-else>
										<div class="name">{{ selectedValue.name }}</div>
										<span>Ukjent holdeplass</span>
									</template>
								</div>
								<div v-else>
									<div class="name">Velg stop</div>
									<span>Søk etter holdeplasser</span>
								</div>
							</template>
							<template #item="{ option }">
								<div class="image" v-if="option?.category?.icon">
									<i class="fas" :class="option?.category?.icon"></i>
								</div>
								<div class="name">
									{{ option.name }}
								</div>
								<span>{{ option.locality }} {{ option.description }}</span>
							</template>
						</select-box>
					</div>
				</div>
			</div>

			<template v-if="!isPublicTransport()">
				<div class="mb-4" v-if="dataStore.currentEditTrip.origin">
					<label class="flex gap-2" @click="flyToLocation('origin')">
						<strong v-if="dataStore.currentEditTrip?.stationary">Hvor var du denne dagen?</strong>
						<strong v-else>Avreisested</strong>
						<button class="button tiny transparent" v-if="dataStore.currentEditTrip.origin?.latitude">
							<i class="fa-solid fa-location-crosshairs"></i>
						</button>
					</label>
					<div class="place form-control mt-1" @click="changeLocation('origin')">
						<template v-if="!dataStore.currentEditTrip.origin.title">
							<span v-if="dataStore.currentEditTrip?.stationary">Velg sted</span>
							<span v-else>Velg sted for avreise</span>
						</template>
						<template v-else>
							<div class="title">{{ dataStore.currentEditTrip.origin.title }}</div>
							<div v-if="dataStore.currentEditTrip.origin.subTitle" class="subtitle">
								{{ dataStore.currentEditTrip.origin.subTitle }}
							</div>
							<div v-if="dataStore.currentEditTrip.origin.type == 'pluscode' && !dataStore.currentEditTrip.origin.subTitle" class="subtitle">Område</div>
							<div v-if="dataStore.currentEditTrip.origin?.type == 'gps'" class="tag" :class="dataStore.currentEditTrip.origin?.type">
								{{ mapStore.formatLat(dataStore.currentEditTrip?.origin?.latitude) }}<br />
								{{ mapStore.formatLng(dataStore.currentEditTrip?.origin?.longitude) }}
							</div>
							<div v-if="dataStore.currentEditTrip?.origin?.type == 'subway'" class="tag" :class="dataStore.currentEditTrip?.origin?.type">T-banestasjon</div>
							<div v-if="dataStore.currentEditTrip?.origin?.type == 'train'" class="tag" :class="dataStore.currentEditTrip?.origin?.type">Togstasjon</div>
						</template>
					</div>
				</div>
				<div class="mb-4" v-if="dataStore.currentEditTrip.destination && (!dataStore.currentEditTrip?.stationary || dataStore.currentEditTrip?.didleave)">
					<label class="flex gap-2" @click="flyToLocation('destination')">
						<strong v-if="dataStore.currentEditTrip?.stationary">Hvor reiste du til?</strong>
						<strong v-else>Ankomststed</strong>
						<button class="button tiny transparent" v-if="dataStore.currentEditTrip.destination?.latitude">
							<i class="fa-solid fa-location-crosshairs"></i>
						</button>
					</label>
					<div class="place form-control mt-1" @click="changeLocation('destination')">
						<template v-if="!dataStore.currentEditTrip.destination.title">
							<span v-if="dataStore.currentEditTrip?.stationary">Velg sted</span>
							<span v-else>Velg sted for ankomst</span>
						</template>
						<template v-else>
							<div class="title">{{ dataStore.currentEditTrip.destination.title }}</div>
							<div v-if="dataStore.currentEditTrip.destination.subTitle" class="subtitle">{{ dataStore.currentEditTrip.destination.subTitle }}</div>
							<div v-if="dataStore.currentEditTrip.destination.type == 'pluscode' && !dataStore.currentEditTrip.destination.subTitle" class="subtitle">Område</div>
							<div v-if="dataStore.currentEditTrip.destination?.type == 'gps'" class="tag" :class="dataStore.currentEditTrip.destination?.type">
								{{ mapStore.formatLat(dataStore.currentEditTrip?.destination?.latitude) }}<br />
								{{ mapStore.formatLng(dataStore.currentEditTrip?.destination?.longitude) }}
							</div>
							<div v-if="dataStore.currentEditTrip?.destination?.type == 'subway'" class="tag" :class="dataStore.currentEditTrip?.destination?.type">T-banestasjon</div>
							<div v-if="dataStore.currentEditTrip?.destination?.type == 'train'" class="tag" :class="dataStore.currentEditTrip?.destination?.type">Togstasjon</div>
						</template>
					</div>
				</div>
				<div class="mt-6 mb-6" v-if="dataStore.currentEditTrip?.stationary">
					<label for="startTime" class="flex items-center">
						<strong>Reiste du herfra?</strong>
						<ToggleSwitch class="ml-4" small v-model="dataStore.currentEditTrip.didleave"></ToggleSwitch>
					</label>
					Dersom du reiste videre fra dette stedet er det fint om du også angir tidspunkt og hvor du reiste.
				</div>
			</template>

			<div v-if="dataStore.currentEditTrip?.stationary">
				<div class="mb-4">
					<label for="startTime"><strong>Når kom du hit?</strong></label>
					<div class="flex gap-2 input-group mb-3 mt-1">
						<input id="startDate" type="date" class="form-control" v-model="dataStore.currentEditTrip.startDateString" />
						<input id="startTime" type="time" class="form-control" v-model="dataStore.currentEditTrip.startTime" placeholder="HH:mm" />
					</div>
				</div>
				<div class="mb-4" v-if="dataStore.currentEditTrip.didleave">
					<label for="endTime"><strong>Når reiste du herfra?</strong></label>
					<div class="flex gap-2 input-group mb-3 mt-1">
						<input id="endTime" type="date" class="form-control" v-model="dataStore.currentEditTrip.endDateString" />
						<input id="endTime" type="time" class="form-control" v-model="dataStore.currentEditTrip.endTime" placeholder="HH:mm" />
					</div>
				</div>
			</div>

			<div class="flex gap-2 mb-4" v-if="!dataStore.currentEditTrip?.stationary">
				<div>
					<label for="startTime"><strong>Avreisetid</strong></label>
					<div class="input-group mb-3 mt-1">
						<input id="startTime" type="time" class="form-control" v-model="dataStore.currentEditTrip.startTime" placeholder="HH:mm" />
					</div>
				</div>
				<div>
					<label for="endTime"><strong>Ankomsttid</strong></label>
					<div class="input-group mb-3 mt-1">
						<input id="endTime" type="time" class="form-control" v-model="dataStore.currentEditTrip.endTime" placeholder="HH:mm" />
					</div>
				</div>
			</div>
		</template>

		<template #footer>
			<div class="w-full flex gap-6">
				<div class="button" @click="goBack()"><i class="fas fa-chevron-left"></i>Tilbake</div>
				<div class="button full green" :class="{ disabled: !hasChanged || !isValid }" @click="saveTrip"><i class="fa-solid fa-floppy-disk"></i>Lagre</div>
			</div>
		</template>
	</BasePage>
</template>

<style>
.pt-select .el-select__wrapper {
	gap: 0px;
	padding: 0px !important;
}
.pt-select {
	padding: 0px;
}
</style>
